import { useState, useEffect } from "react";
import { Box, Container, Button } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { IProgress, ISessionProps, SessionStatus, OSType } from "../../../main/components/IProgress";
import { Typography } from "@appsflyer/fe-ui-core";
import transition from "../../../main/components/transition";
import { amplitudeTrack_continue, sendGtagOnce } from "../../../services/utils";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import { DJSimpleCodeBlock, ICodeBlock } from "../../../main/components/codeblock";

const StartSDKReactNative = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeStep, setActiveStep] = useState(0);
  const [devKey, setDevKey] = useState();
  const [appId, setAppId] = useState<string>("");

  useEffect(() => {
    if (location.state) {
      const state: ISessionProps = location.state as ISessionProps;
      setAppId(state?.appID ?? "");

      const lsDevKey = localStorage.getItem("devKey");
      if (lsDevKey) {
        setDevKey(JSON.parse(lsDevKey));
      }
    }
  }, [location.state]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleClick = () => {
    const progressArr: IProgress[] = location.state?.progress.slice();
    
    // Check if "Start SDK" progress item already exists
    const startSDKExists = progressArr.some(
      (item) => item.text === "progress_startsdk_startSDK" || item.id === "reactnative_startSDK"
    );
    
    // If it doesn't exist, add it (for iOS which goes directly from InstallSDK)
    if (!startSDKExists) {
      progressArr.push({
        id: "reactnative_startSDK",
        text: "progress_startsdk_startSDK",
        order: progressArr.length + 1,
        startTime: new Date(),
        updateTime: new Date(),
      });
    }
    
    // Update the last progress item (like Android does)
    // Android updates the last progress item directly
    if (progressArr.length > 0) {
      progressArr[progressArr.length - 1].updateTime = new Date();
    }

    const progressState: ISessionProps = {
      ...location.state,
      progress: progressArr,
      sessionStatus: SessionStatus.ImplementedNotTested,
      currentPage: location.state?.currentPage + 1,
    };

    // send gtag for implemented not test
    sendGtagOnce(
      "implemented_not_tested",
      "StartSDKReactNative",
      location.state?.sessionId
    );

    amplitudeTrack_continue(location.state);

    // Navigate to testsdktype (choose testing type)
    const basePath = location.pathname.startsWith("/migration") ? "/migration" : "";
    navigate(`${basePath}/startsdk/${location.state?.os}/testsdktype`, {
      state: progressState,
    });
  };

  // Determine OS for conditional labels and content
  const isAndroid = location.state?.os === OSType.ReactNativeAndroid;

  // Code block strings
  const codeStep1 = isAndroid 
    ? `{
  "expo": {
    ...
    "android": {
      "adaptiveIcon": { ...
      },
      "package": "${appId}",
      "softwareKeyboardLayoutMode": "pan",
      "intentFilters": [...
      ]
    },
    ...
  }
}` 
    : `"ios": {
      "bitcode": false,
      "supportsTablet": true,
      "bundleIdentifier": "" // Enter your bundle identifier here
    },`;
  
  const codeStep2 = `appsFlyer.initSdk({
  devKey: '${devKey}',
  isDebug: true,
  appId: '${appId}',
});`;
  

  const codeBlockArray: ICodeBlock[] = [
    {
      codePrimary: codeStep1,
      language: "json",
      showLineNumbers: true,
    },
    {
      codePrimary: codeStep2,
      language: "typescript",
      showLineNumbers: true,
    },
  ];

  const steps = [
    {
      label: isAndroid 
        ? t("StartSDK_StartSDK_reactnative_android_step1_label")
        : t("StartSDK_StartSDK_reactnative_ios_step1_label"),
      component: (
        <>
          <Typography mb={1.5}>
            {isAndroid 
            ? <Trans 
                i18nKey="StartSDK_StartSDK_reactnative_android_step1_description"
                components={{ code: <code /> }}
              /> 
            : <Trans 
                i18nKey="StartSDK_StartSDK_reactnative_ios_step1_description"
                components={{ code: <code /> }}
              />}
          </Typography>
          <DJSimpleCodeBlock {...codeBlockArray[0]}></DJSimpleCodeBlock>
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
    {
      label: "Set your dev key and app ID",
      component:
      <>
        <DJSimpleCodeBlock {...codeBlockArray[1]}></DJSimpleCodeBlock>
      </>,
      shouldDisplayNavigationButtons: true,
    },
  ];

  return (
    <Container maxWidth={"lg"}>
      <Box padding={3} style={{ minHeight: "50vh" }}>
        <Typography variant="h1">
          {t("start_sdk")}
        </Typography>
        <Stepper
          activeStep={activeStep}
          style={{ border: "none" }}
          orientation="vertical"
        >
          {steps.map(({ label, component, shouldDisplayNavigationButtons }) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>
                {component}
                {shouldDisplayNavigationButtons && (
                  <Box sx={{ mt: 2 }}>
                    {activeStep !== 0 && (
                      <Button
                        variant="text"
                        color="primary"
                        onClick={handleBack}
                      >
                        {t("general_Back")}
                      </Button>
                    )}
                    {activeStep !== steps.length - 1 && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                      >
                        {t("general_Next")}
                      </Button>
                    )}
                  </Box>
                )}
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignSelf="end"
        justifySelf="end"
        marginTop={10}
        textAlign="end"
        width={"100%"}
      >
        <Button
          variant="outlined"
          color="secondary"
          size="medium"
          style={{ marginRight: "5px" }}
          onClick={() => navigate(-1)}
        >
          {t("general_Back")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="medium"
          onClick={handleClick}
        >
          {t("general_Continue")}
        </Button>
      </Box>
    </Container>
  );
};

export default transition(StartSDKReactNative);

